import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const NewsSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const items = data?.items || [];
  const layout = data?.layout || 'grid';
  const spacing = data?.spacing || 'spaced';
  
  if (items.length === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const gapClass = spacing === 'flush' ? 'gap-0' : 'gap-6';

  // List layout - full width cards stacked
  const renderListLayout = () => (
    <div className={cn("space-y-0", spacing === 'spaced' ? 'space-y-6' : 'divide-y', t.border)}>
      {items.map((item, idx) => (
        <article 
          key={idx} 
          className={cn(
            "overflow-hidden",
            spacing === 'spaced' && "rounded-xl border",
            spacing === 'spaced' && t.border,
            t.bg
          )}
        >
          <div className="flex flex-col md:flex-row">
            {item.image && (
              <div className="md:w-1/3 aspect-video md:aspect-[4/3] overflow-hidden flex-shrink-0">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className={cn("flex-1 p-6", !item.image && "py-8")}>
              <div className="flex items-center gap-3 mb-3">
                <span 
                  className="text-xs font-medium px-3 py-1 rounded-full"
                  style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
                >
                  {formatDate(item.date)}
                </span>
              </div>
              <h3 className={cn("text-xl font-bold mb-3", t.text)}>{item.title}</h3>
              <p className={cn("leading-relaxed line-clamp-3", t.textMuted)}>{item.content}</p>
              {item.link && (
                <a 
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 font-medium"
                  style={{ color: primaryColor }}
                >
                  Read more <ChevronRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  );

  // Grid layout - 2 or 3 columns
  const renderGridLayout = () => (
    <div className={cn("grid md:grid-cols-2 lg:grid-cols-3", gapClass)}>
      {items.map((item, idx) => (
        <article 
          key={idx} 
          className={cn(
            "overflow-hidden",
            spacing === 'spaced' && "rounded-xl border",
            spacing === 'spaced' && t.border,
            t.bg
          )}
        >
          {item.image && (
            <div className="aspect-video overflow-hidden">
              <img 
                src={item.image} 
                alt={item.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          )}
          <div className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <span 
                className="text-xs font-medium px-3 py-1 rounded-full"
                style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
              >
                {formatDate(item.date)}
              </span>
            </div>
            <h3 className={cn("text-lg font-bold mb-2", t.text)}>{item.title}</h3>
            <p className={cn("text-sm leading-relaxed line-clamp-3", t.textMuted)}>{item.content}</p>
            {item.link && (
              <a 
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-sm font-medium"
                style={{ color: primaryColor }}
              >
                Read more <ChevronRight className="w-3 h-3" />
              </a>
            )}
          </div>
        </article>
      ))}
    </div>
  );

  // Bento layout - featured first item larger
  const renderBentoLayout = () => (
    <div className={cn("grid md:grid-cols-2 lg:grid-cols-3", gapClass)}>
      {items.map((item, idx) => {
        const isFeatured = idx === 0;
        return (
          <article 
            key={idx} 
            className={cn(
              "overflow-hidden",
              spacing === 'spaced' && "rounded-xl border",
              spacing === 'spaced' && t.border,
              t.bg,
              isFeatured && "md:col-span-2 lg:col-span-2 lg:row-span-2"
            )}
          >
            {item.image && (
              <div className={cn(
                "overflow-hidden",
                isFeatured ? "aspect-[16/9]" : "aspect-video"
              )}>
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            )}
            <div className={cn("p-5", isFeatured && "p-6")}>
              <div className="flex items-center gap-3 mb-3">
                <span 
                  className="text-xs font-medium px-3 py-1 rounded-full"
                  style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
                >
                  {formatDate(item.date)}
                </span>
              </div>
              <h3 className={cn(
                "font-bold mb-2", 
                t.text,
                isFeatured ? "text-2xl" : "text-lg"
              )}>{item.title}</h3>
              <p className={cn(
                "leading-relaxed",
                t.textMuted,
                isFeatured ? "line-clamp-4" : "text-sm line-clamp-3"
              )}>{item.content}</p>
              {item.link && (
                <a 
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  Read more <ChevronRight className="w-3 h-3" />
                </a>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );

  return (
    <section id="news" className={cn("py-20 px-4", t.card)}>
      <div className={cn("mx-auto", layout === 'list' ? "max-w-4xl" : "max-w-6xl")}>
        <h2 className={cn("text-4xl md:text-5xl font-bold mb-12 text-center", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
          {data.title || 'News'}
        </h2>
        {layout === 'list' && renderListLayout()}
        {layout === 'grid' && renderGridLayout()}
        {layout === 'bento' && renderBentoLayout()}
      </div>
    </section>
  );
};


